import { resolveListingPrice } from "../../Home/MarketplaceNewUI/marketplaceHomeFeaturedFromApi";

export function getTicketsEventPriceKey(event) {
  if (!event) return null;
  const tevoId = event?.id ?? event?.event?.[0]?.id;
  if (tevoId != null && tevoId !== "") return String(tevoId);
  if (event?._id != null) return String(event._id);
  return null;
}

export function isPriceFilterActive(priceRange, freeOnly) {
  return freeOnly || priceRange[0] > 0 || priceRange[1] < 500;
}

export function getEventListPrice(event, listingPrices = {}) {
  if (!event) return null;
  const key = getTicketsEventPriceKey(event);
  if (key && listingPrices[key] != null && listingPrices[key] > 0) {
    return listingPrices[key];
  }
  const resolved = resolveListingPrice(
    event._isUserCreated ? event : event,
    event._isUserCreated ? undefined : undefined
  );
  return resolved > 0 ? resolved : null;
}

export function getEventDateMs(event) {
  if (!event) return 0;
  const raw = event._isUserCreated && event.endDate ? event.endDate : event.occurs_at;
  const d = new Date(raw);
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function getEventDateForFilter(event) {
  if (!event) return null;
  const raw =
    event._isUserCreated && event.endDate ? event.endDate : event.occurs_at;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function eventMatchesDateRange(event, startdate, enddate) {
  if (!startdate && !enddate) return true;
  const eventDate = getEventDateForFilter(event);
  if (!eventDate) return false;
  if (startdate) {
    const start = new Date(startdate);
    start.setHours(0, 0, 0, 0);
    if (eventDate < start) return false;
  }
  if (enddate) {
    const end = new Date(enddate);
    end.setHours(23, 59, 59, 999);
    if (eventDate > end) return false;
  }
  return true;
}

/** Sidebar "When" presets (must match TICKETS_DATE_PRESETS labels). */
export function eventMatchesDatePreset(event, preset) {
  const normalized = (preset || "").trim();
  if (!normalized || normalized === "Any Dates") return true;

  const eventDate = getEventDateForFilter(event);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!eventDate) return false;
  if (eventDate < today) return false;

  switch (normalized) {
    case "This Weekend": {
      const end = new Date(today);
      const daysUntilSunday = (7 - today.getDay()) % 7;
      end.setDate(today.getDate() + (daysUntilSunday === 0 ? 7 : daysUntilSunday));
      end.setHours(23, 59, 59, 999);
      return eventDate >= today && eventDate <= end;
    }
    case "This Week": {
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
      endOfWeek.setHours(23, 59, 59, 999);
      return eventDate >= today && eventDate <= endOfWeek;
    }
    case "This Month": {
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      return eventDate >= today && eventDate <= endOfMonth;
    }
    case "Next Month": {
      const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      endOfNextMonth.setHours(23, 59, 59, 999);
      return eventDate >= startOfNextMonth && eventDate <= endOfNextMonth;
    }
    case "Next 60 Days": {
      const end = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
      end.setHours(23, 59, 59, 999);
      return eventDate >= today && eventDate <= end;
    }
    default:
      return true;
  }
}

export function sortTicketEvents(events, sortBy, listingPrices = {}) {
  const sorted = [...events];
  if (sortBy === "Date: Soonest") {
    sorted.sort((a, b) => getEventDateMs(a) - getEventDateMs(b));
  } else if (sortBy === "Price: Low to High") {
    sorted.sort(
      (a, b) =>
        (getEventListPrice(a, listingPrices) ?? Infinity) -
        (getEventListPrice(b, listingPrices) ?? Infinity)
    );
  } else if (sortBy === "Price: High to Low") {
    sorted.sort(
      (a, b) =>
        (getEventListPrice(b, listingPrices) ?? -Infinity) -
        (getEventListPrice(a, listingPrices) ?? -Infinity)
    );
  } else {
    sorted.sort((a, b) => {
      let dateA = new Date(a.occurs_at || a.updated_at || 0);
      let dateB = new Date(b.occurs_at || b.updated_at || 0);
      if (a._isUserCreated && a._id && typeof a._id === "string" && a._id.length === 24) {
        if (isNaN(dateA.getTime())) {
          try {
            dateA = new Date(parseInt(a._id.substring(0, 8), 16) * 1000);
          } catch {
            dateA = new Date(0);
          }
        }
      }
      if (b._isUserCreated && b._id && typeof b._id === "string" && b._id.length === 24) {
        if (isNaN(dateB.getTime())) {
          try {
            dateB = new Date(parseInt(b._id.substring(0, 8), 16) * 1000);
          } catch {
            dateB = new Date(0);
          }
        }
      }
      return dateB - dateA;
    });
  }
  return sorted;
}

export function applyClientPriceFilters(
  events,
  priceRange,
  freeOnly,
  listingPrices = {}
) {
  const active = isPriceFilterActive(priceRange, freeOnly);
  return events.filter((event) => {
    const price = getEventListPrice(event, listingPrices);
    if (freeOnly) return price === 0;
    if (!active) return true;
    if (price == null) return false;
    return price >= priceRange[0] && price <= priceRange[1];
  });
}
